import { Component } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { catchError, firstValueFrom, forkJoin } from 'rxjs';
import { GenericCrudAddComponent } from 'src/app/libs/components/generic-crud/generic-crud-add.component';
import { ProductTechnicalFeatures, TId, TechnicalFeature, TechnicalFeaturesGroups } from 'src/app/models';
import { ProductsTechnicalFeaturesAddComponent } from 'src/app/routes/products/configuration/products-technical-features/products-technical-features-add/products-technical-features-add.component';

@Component({
    selector: 'app-product-technical-features-add',
    templateUrl: './product-technical-features-add.component.html',
    styleUrl: './product-technical-features-add.component.sass',
    standalone: false
})
export class ProductTechnicalFeaturesAddComponent extends GenericCrudAddComponent<ProductTechnicalFeatures> {
  famillyTechnicalFeaturesIds: TId[] = [];
  technicalFeaturesValues: TId[] = [];
  dataSource: TechnicalFeature[] | TechnicalFeaturesGroups[] = [];
  technicalFeaturesOfGroup: { [key: TId]: TId[] } = {};
  addOption?: string;
  createTechnicalFeature: any;

  constructor() {
    super();

  }

  override async preFetch(): Promise<void> {
    this.resource = this.api.resources.product.productTechnicalFeatures(this.initialFormValues.id);
    this.addOption = this.initialFormValues.addOption;
    this.famillyTechnicalFeaturesIds = (await firstValueFrom(this.api.resources.product.productTechnicalFeatures(this.initialFormValues.id).getAll())).map(tfg => tfg.technicalFeatureId);
    if(this.addOption === 'addTechnicalfeatureGroup') {
      const groups = await firstValueFrom(this.api.resources.product.technicalFeatureGroups.getAll());
      for (const group of groups) {
        const features = await firstValueFrom(this.api.resources.product.technicalFeaturesOfGroup(group.id).getAll());
        this.technicalFeaturesOfGroup[group.id] = features.map(f => f.id);
      }
      this.dataSource = groups;
    } else {
      this.createTechnicalFeature = ProductsTechnicalFeaturesAddComponent;
      this.dataSource = await firstValueFrom(this.api.resources.product.technicalfeatures.getAll());
    }

  }
  initFormGroup(): FormGroup<any> {
    const group = this.fb.group({
      technicalFeature: [undefined, [this.technicalfeatureInFamillyValidator(), this.allTechnicalFeaturesOfGroupInFamillyValidator()]],
    });
    return group;
  }
  technicalfeatureInFamillyValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const id = control.value;
      if(this.addOption === 'addTechnicalfeatureGroup') return null;
      return (!this.famillyTechnicalFeaturesIds.some(famillyTechnicalFeaturesId => famillyTechnicalFeaturesId === id)) ? null : { 'technicalFeatureIsAlreadyInThisFamily': true };
    };
  }

  allTechnicalFeaturesOfGroupInFamillyValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const id = control.value;

      if(this.addOption !== 'addTechnicalfeatureGroup') return null;

      const featuresOfGroup = this.technicalFeaturesOfGroup[id];
      if (!featuresOfGroup) return null;

      const allInFamily = featuresOfGroup.every(featureId => this.famillyTechnicalFeaturesIds.includes(featureId));

      return allInFamily ? { 'allTechnicalFeaturesAreAlreadyInThisFamily': true } : null;
    };
  }

  async onValueChange(event: any): Promise<void> {
    if(this.addOption === 'addTechnicalfeatureGroup'){
      const featuresOfGroup = this.technicalFeaturesOfGroup[event];
      this.technicalFeaturesValues = featuresOfGroup.filter(tfg => !this.famillyTechnicalFeaturesIds.includes(tfg));
      return;
    }
    this.technicalFeaturesValues.push(event);
  }

  override async createEntity(): Promise<void> {
    try {
      if (await this.validateEntity()) {
        // Create an array of POST request observables
        const posts = this.technicalFeaturesValues.map(technicalFeatureId =>
          this.resource!.post({ technicalFeatureId }).pipe(
            catchError(error => {
              this.alertSrv.error(error);
              throw error; // Ensure the error is propagated
            })
          )
        );

        // Use forkJoin to execute all POST requests in parallel and get the results
        const results = await firstValueFrom(forkJoin(posts));

        // Emit the results array
        this.entityCreated.emit(results);
      }
    } catch (error) {
      this.alertSrv.error(error);
    }
  }
}
